import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { AdminLayout } from "./admin/AdminLayout";
import { LoginPage } from "./admin/LoginPage";
import { AcceptInvitePage } from "./admin/AcceptInvitePage";
import { ForgotPasswordPage } from "./admin/ForgotPasswordPage";
import { ResetPasswordPage } from "./admin/ResetPasswordPage";
import { DashboardPage } from "./admin/DashboardPage";
import { LibraryPage } from "./admin/LibraryPage";
import { VideoDetailPage } from "./admin/VideoDetailPage";
import { CampaignsPage } from "./admin/CampaignsPage";
import { WorkspaceLayout } from "./admin/workspace/WorkspaceLayout";
import { OverviewTab } from "./admin/workspace/WorkspaceTabs";
import { SurveysTab } from "./admin/workspace/SurveysTab";
import { DeliverablesTab } from "./admin/workspace/DeliverablesTab";
import { AccomplishmentsTab } from "./admin/workspace/AccomplishmentsTab";
import { MediaTab } from "./admin/workspace/MediaTab";
import { TeamTab } from "./admin/workspace/TeamTab";
import { PlaylistsTab } from "./admin/workspace/PlaylistsTab";
import { ReportsTab } from "./admin/workspace/ReportsTab";
import { EventsTab } from "./admin/workspace/EventsTab";
import { PlaylistsPage } from "./admin/PlaylistsPage";
import { InternalPlaylistsPage } from "./admin/InternalPlaylistsPage";
import { PlaylistBuilderPage } from "./admin/PlaylistBuilderPage";
import { IssuancesPage } from "./admin/IssuancesPage";
import { ComposeIssuancePage } from "./admin/ComposeIssuancePage";
import { IssuanceDetailPage } from "./admin/IssuanceDetailPage";
import { AnalyticsPage } from "./admin/AnalyticsPage";
import { ViewerAnalyticsPage } from "./admin/ViewerAnalyticsPage";
import { UsersPage } from "./admin/UsersPage";
import { SettingsPage } from "./admin/SettingsPage";
import { EventsPage } from "./admin/EventsPage";
import { NotFoundPage } from "./admin/NotFoundPage";
import { DemoPacksPage } from "./admin/DemoPacksPage";
import { CoverPage } from "./viewer/CoverPage";
import { RatePage } from "./viewer/RatePage";
import { WatchPage } from "./viewer/WatchPage";
import { DonePage } from "./viewer/DonePage";
import { DemoPackLandingPage } from "./viewer/DemoPackLandingPage";

// S14 cutover (reversible). When FLAG_WORKSPACE_STRICT is ON, the global
// cross-campaign pages are retired: their routes redirect to the campaign
// picker so bookmarks/deep-links can't bypass the "enter a workspace first"
// mandate. Rendered inside AdminLayout's AuthProvider, and only after auth has
// loaded (Inner gates on loading), so flags are resolved — no redirect flash.
// The builder + compose routes are intentionally NOT wrapped: they're launched
// from within a workspace (campaign-scoped) and must stay reachable.
function RetireWhenStrict({ children }: { children: ReactNode }) {
  const { flags } = useAuth();
  // Require the base workspace flow too (see AdminLayout) so strict-alone can't
  // strand the user.
  if (flags.workspaceStrict && flags.campaignWorkspaces) {
    return <Navigate to="/admin/campaigns" replace />;
  }
  return <>{children}</>;
}

export function App() {
  const loc = useLocation();
  useEffect(() => {
    // Toggle admin class on <html> for admin routes; viewer routes use light defaults.
    const root = document.documentElement;
    const isAdmin =
      loc.pathname.startsWith("/admin") ||
      loc.pathname === "/" ||
      ["/login", "/accept-invite", "/forgot", "/reset"].includes(loc.pathname);
    root.classList.toggle("admin", isAdmin);
  }, [loc.pathname]);

  return (
    <Routes>
      {/* Auth */}
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset" element={<ResetPasswordPage />} />

      {/* Admin */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<RetireWhenStrict><DashboardPage /></RetireWhenStrict>} />
        <Route path="library" element={<RetireWhenStrict><LibraryPage /></RetireWhenStrict>} />
        {/* Video DETAIL stays reachable under strict — it's the canonical
            per-video page (tags/approval/versions/attachments) that the
            workspace Media tab links to; only the global BROWSE page retires.
            Wrapping it bounced every Media-tab card to the campaign picker. */}
        <Route path="library/:videoId" element={<VideoDetailPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        {/* CEO Rev 1.0 — Campaign Workspace (S3). Gated by the campaignWorkspaces
            flag inside WorkspaceLayout (deploy-dark). Tabs filled by S4–S12. */}
        <Route path="campaigns/:campaignId" element={<WorkspaceLayout />}>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewTab />} />
          <Route path="events" element={<EventsTab />} />
          <Route path="media" element={<MediaTab />} />
          <Route path="deliverables" element={<DeliverablesTab />} />
          <Route path="accomplishments" element={<AccomplishmentsTab />} />
          <Route path="reports" element={<ReportsTab />} />
          <Route path="playlists" element={<PlaylistsTab />} />
          <Route path="team" element={<TeamTab />} />
          <Route path="surveys" element={<SurveysTab />} />
        </Route>
        <Route path="playlists" element={<RetireWhenStrict><PlaylistsPage /></RetireWhenStrict>} />
        <Route path="internal" element={<RetireWhenStrict><InternalPlaylistsPage /></RetireWhenStrict>} />
        {/* Builder stays reachable under strict — launched from the workspace
            Playlists tab (New / Edit videos), campaign-scoped. */}
        <Route path="playlists/new" element={<PlaylistBuilderPage />} />
        <Route path="playlists/:playlistId" element={<PlaylistBuilderPage />} />
        <Route path="issuances" element={<RetireWhenStrict><IssuancesPage /></RetireWhenStrict>} />
        {/* Compose + detail stay reachable — compose is launched pre-scoped from
            the workspace Audience card's "Send Playlist". */}
        <Route path="issuances/new" element={<ComposeIssuancePage />} />
        <Route path="issuances/:issuanceId" element={<IssuanceDetailPage />} />
        <Route path="demo-packs" element={<RetireWhenStrict><DemoPacksPage /></RetireWhenStrict>} />
        <Route path="analytics" element={<RetireWhenStrict><AnalyticsPage /></RetireWhenStrict>} />
        <Route path="analytics/viewer/:viewerId" element={<RetireWhenStrict><ViewerAnalyticsPage /></RetireWhenStrict>} />
        <Route path="users" element={<UsersPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="settings/*" element={<SettingsPage />} />
      </Route>

      {/* Viewer */}
      <Route path="/view/:token" element={<CoverPage />} />
      <Route path="/view/:token/rate" element={<RatePage />} />
      <Route path="/view/:token/watch" element={<WatchPage />} />
      <Route path="/view/:token/watch/:videoRef" element={<WatchPage />} />
      <Route path="/view/:token/done" element={<DonePage />} />

      {/* Audit V1: public demo-pack landing. /p/:slug is the public-playlist
          alias — a visibility=public playlist auto-manages a backing shared
          link with its publicSlug, so both paths serve the same flow. */}
      <Route path="/pack/:slug" element={<DemoPackLandingPage />} />
      <Route path="/p/:slug" element={<DemoPackLandingPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
